// End-user copy module (fn-48) — EVERY client-facing string lives
// here, centralized as the future i18n seam (hardcoded en for v1).
//
// Rules this module enforces by existing:
//   - No raw IDs, status-enum strings, or internal vocabulary ever
//     reach end-user UI — components render ONLY what this module
//     (and `humanize.ts`) returns.
//   - Friendly error copy replaces transport errors (`errorMessage(err)`
//     is operator-grade; clients get these strings instead).
//
// Tool-name humanization lives in the sibling `humanize.ts`.
/**
 * Agent display-name fallback. Used whenever an `agent_id` can't be
 * resolved through `listAgents` (renamed / deleted / paginated-out /
 * adapter without `listAgents`). An id must never render instead.
 */
export const FALLBACK_AGENT_NAME = 'Assistant';
/**
 * Status-derived row line for the conversation list. End users never
 * see the status enum itself — every CP status maps to a plain-
 * language line (all four terminals collapse to "Ended"; the detail
 * distinction is operator-grade).
 */
export const STATUS_LINES = {
    pending: 'Starting…',
    running: 'Working on it…',
    awaiting_user: 'Waiting for your reply',
    awaiting_approval: 'Waiting for your approval',
    completed: 'Ended',
    failed: 'Ended',
    cancelled: 'Ended',
    expired: 'Ended',
};
/**
 * Resolve the status line for a row. The adapter passes wire statuses
 * through without validating the vocabulary, so an unrecognized value
 * is possible — fall back to the active-work line rather than leaking
 * the raw enum or rendering blank.
 */
export function statusLine(status) {
    return STATUS_LINES[status] ?? STATUS_LINES.running;
}
/** Pinned group heading in the conversation list. */
export const NEEDS_ATTENTION_HEADING = 'Needs your attention';
/** Second conversation-list section: everything not needing attention. */
export const RECENT_HEADING = 'Recent';
/** Heading for the client-side, localStorage-backed favourites section. */
export const STARRED_HEADING = 'Starred';
/**
 * Accessible name for a row's star toggle. Carries the friendly subject (never
 * a raw id or status enum — the no-id/no-enum sweep covers this label) so a
 * screen-reader user can tell the per-row buttons apart.
 */
export function starToggleLabel(subject, starred) {
    const verb = starred ? 'Unstar' : 'Star';
    return subject.length > 0 ? `${verb} ${subject}` : `${verb} this conversation`;
}
/**
 * Needs-attention row marker text (rendered next to the accent dot —
 * the marker is ALWAYS text + dot, never color alone). Only the two
 * attention statuses carry one; `attentionMarker` returns null for
 * everything else so callers fall back to `statusLine`.
 */
export const NEEDS_ATTENTION_MARKERS = {
    awaiting_user: 'Needs your reply',
    awaiting_approval: 'Needs your approval',
};
/** Marker text for a needs-attention status; null when none applies. */
export function attentionMarker(status) {
    if (status === 'awaiting_user' || status === 'awaiting_approval') {
        return NEEDS_ATTENTION_MARKERS[status];
    }
    return null;
}
/** Conversation-list empty state (zero conversations overall). */
export const EMPTY_CONVERSATIONS_TITLE = 'No conversations yet';
export const EMPTY_CONVERSATIONS_BODY = 'When you start a conversation, it shows up here — and you can pick up where you left off any time.';
/** Start-conversation call to action (empty state + start flow). */
export const START_CONVERSATION_CTA = 'Start a conversation';
/** Quiet loading line while the first conversations fetch is in flight. */
export const LOADING_CONVERSATIONS = 'Loading your conversations…';
/** Relative-time label for anything under a minute old. */
export const JUST_NOW = 'just now';
/** Terminal copy that replaces the composer in `<Chat>`. */
export const CONVERSATION_ENDED = 'This conversation has ended';
/** Quiet cap note when the list shows only the most recent page. */
export const SHOWING_RECENT_NOTE = 'Showing recent conversations';
/** Quiet inline note while the live stream is reconnecting. */
export const RECONNECTING_NOTE = 'Reconnecting…';
/** Subject fallback when neither a title nor a user message exists. */
export function conversationWith(agentName) {
    return `Conversation with ${agentName}`;
}
// ── Chat strings (fn-48.3) ──────────────────────────────────────────
/** Activity disclosure toggle labels (chevron + this text). */
export const SHOW_DETAILS = 'Show details';
export const HIDE_DETAILS = 'Hide details';
/**
 * Collapsed summary for a COMPLETED multi-step activity run. Single
 * steps keep their own friendly line ("Using matter search…") — the
 * count form only earns its place when it actually compresses.
 *
 * `duration` (fn-49.3, R4): the completed TURN's formatted duration,
 * appended quietly ("Worked on 3 steps · 12s"). Omitted when the turn
 * is in flight or its duration is unknown.
 */
export function workedOnSteps(count) {
    return count === 1 ? 'Worked on 1 step' : `Worked on ${count} steps`;
}
/**
 * The standalone duration suffix (" · 12s") for rendering as its OWN
 * node next to a headline — separable so the UI can style it muted
 * independently (CodeRabbit PR #65 round 1).
 */
export function durationSuffix(duration) {
    return ` · ${duration}`;
}
// ── Turn durations (fn-49.3, R4) ────────────────────────────────────
/** Sub-second turn duration — no false precision. */
export const SUB_SECOND_DURATION = '<1s';
/** Whole-second duration under a minute ("12s"). */
export function secondsDuration(seconds) {
    return `${seconds}s`;
}
/** Minute-scale duration ("1m 23s"; exact minutes drop the seconds). */
export function minutesDuration(minutes, seconds) {
    return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`;
}
/** Quiet duration suffix on a headline ("Using matter search… · 12s"). */
export function withDuration(line, duration) {
    return `${line} · ${duration}`;
}
/**
 * Quiet reflection of a HITL decision on an activity step. Renders in
 * the activity line / disclosure detail — never a status banner.
 */
export function decisionNote(decision) {
    return decision === 'allow' ? 'You approved this' : 'You declined this';
}
/** Friendly line for an orphan tool result (its tool_use never seen). */
export const COMPLETED_STEP_LINE = 'Completed a step';
/** Composer disabled reason while a HITL gate is open. */
export const RESPOND_TO_CONTINUE = 'Respond to the request above to continue';
/**
 * Composer disabled reason while the agent is actively responding (session
 * status `running`). Sending a second message mid-turn 409s on every backend,
 * so the composer is quietly held until the turn settles.
 */
export const AGENT_RESPONDING = 'The agent is responding…';
/** Quiet view-only note replacing the composer under `readOnly`. */
export const CHAT_VIEW_ONLY = 'You can view this conversation, but you can’t reply here';
/** Chat composer accessible label (visually hidden). */
export const COMPOSER_LABEL = 'Message';
/** Chat composer placeholder. */
export const COMPOSER_PLACEHOLDER = 'Type your message…';
/** Chat composer send button labels. */
export const SEND_LABEL = 'Send';
export const SENDING_LABEL = 'Sending…';
/** Back affordance in the chat header. */
export const BACK_LABEL = 'Back';
/**
 * Friendly error copy — keyed by situation, not transport. Components
 * map `DecisionError.kind` / fetch failures onto these; the raw
 * `Error.message` never reaches a client.
 */
export const ERROR_COPY = {
    /** Transport failure on send / decide. */
    network: 'Couldn’t send — try again',
    /**
     * Send rejected because the agent is still responding (CP 409
     * `session_busy`). Distinct from `network`: the message wasn't sent, but
     * nothing is broken — retry once the turn settles.
     */
    busy: 'The agent is still responding — your message wasn’t sent. Try again in a moment.',
    /** `DecisionError` kind `conflict` — decided elsewhere. */
    conflict: 'This was already handled',
    /** Transport failure on an approval decision / answer submit. */
    decision: 'Couldn’t send your decision — try again',
    /** `startSession` failure — inputs are preserved on the form. */
    startFailure: 'Couldn’t start the conversation — try again',
    /** Catch-all for anything unclassified. */
    generic: 'Something went wrong — try again',
};
// ── Start conversation (fn-48.5) ────────────────────────────────────
/** Agent-picker heading on the start screen — warm, simple. */
export const START_PICKER_HEADING = 'Who do you want to talk to?';
/** Message field label on the start screen. */
export const START_MESSAGE_LABEL = 'What can we help with?';
/** Optional subject field label + placeholder. */
export const START_SUBJECT_LABEL = 'Subject (optional)';
export const START_SUBJECT_PLACEHOLDER = 'What’s this about?';
/** Quiet loading line while the agent list loads. */
export const LOADING_AGENTS = 'Finding who’s available…';
/** Friendly empty state when no agents are available to talk to. */
export const NO_AGENTS_AVAILABLE = 'No one is available to talk to right now — check back soon.';
/** Start submit button labels. */
export const START_SUBMIT_LABEL = 'Start conversation';
export const START_SUBMITTING_LABEL = 'Starting…';
/**
 * Memory toggles on the start screen (fn-3) — per-scope memory-store
 * attachment. Plain language, no "scope"/"store" jargon; the matter
 * toggle only renders when the host supplied a matter context.
 */
export const START_MEMORY_LEGEND = 'Memory (optional)';
export const START_MEMORY_HINT = 'Let the agent remember and use what it learns.';
export const START_MEMORY_USER_LABEL = 'My memory';
export const START_MEMORY_FIRM_LABEL = 'Firm memory';
export const START_MEMORY_MATTER_LABEL = 'Matter memory';
// ── Action cards (fn-48.4) ──────────────────────────────────────────
/**
 * Approval-card heading: plain-language framing of the gated tool.
 * `action` is the tool's infinitive phrase from `toolInfinitive`
 * ("run a command", "use matter search").
 */
export function wantsTo(agentName, action) {
    return `${agentName} wants to ${action}`;
}
/**
 * Consequence-forward approval-card body. Pessimistic by design:
 * nothing happens until the human decides.
 */
export const APPROVAL_CONSEQUENCE = 'Nothing happens until you decide — approving lets the agent take this action on your behalf.';
/** Approval-card action labels. */
export const APPROVE_LABEL = 'Approve';
export const DECLINE_LABEL = 'Decline…';
export const CONFIRM_DECLINE_LABEL = 'Decline';
export const CANCEL_LABEL = 'Cancel';
export const DECLINE_REASON_LABEL = 'Add a note (optional)';
/** Pessimistic in-flight label while a decision round-trips. */
export const DECISION_SENDING = 'Sending…';
// ── Review-matter confirmation card (fn-69.4) ───────────────────────
/**
 * Review-matter card heading: the agent has assembled a matter draft
 * and is asking the user to confirm it before anything is created.
 */
export function reviewMatterHeading(agentName) {
    return `${agentName} has a matter ready for you to review`;
}
/**
 * Consequence-forward body for the review-matter card. Pessimistic:
 * nothing is created until the user confirms.
 */
export const REVIEW_MATTER_BODY = 'Check the details below. Confirming lets the agent go ahead and create this matter on your behalf.';
/** Empty-draft note when the assembled matter carries no displayable fields. */
export const REVIEW_MATTER_EMPTY = 'The agent didn’t include any matter details to show.';
/** Review-matter action label (the confirm path supplies the matter as form data). */
export const CONFIRM_MATTER_LABEL = 'Confirm matter';
/**
 * ask_user form heading: the agent is asking the user to answer.
 * Count-aware so a multi-question form reads naturally.
 */
export function asksYou(agentName, questionCount) {
    return questionCount === 1
        ? `${agentName} has a question for you`
        : `${agentName} has ${questionCount} questions for you`;
}
/** The free-text choice appended to every question's options. */
export const OTHER_OPTION_LABEL = 'Other';
export const OTHER_TEXT_PLACEHOLDER = 'Type your answer…';
/** ask_user form submit button label. */
export const SUBMIT_ANSWERS_LABEL = 'Send answers';
/** Quiet pessimistic success note once the answers round-trip lands. */
export const ANSWERS_SENT = 'Answers sent';
/**
 * Inline per-question validation hints — mirror the CP validator's
 * rules (every question answered; multiSelect ≥1; "Other" text
 * non-empty trimmed; no duplicate picks) without ever surfacing a 400.
 */
export const ASK_USER_HINTS = {
    chooseOne: 'Choose an option to continue',
    chooseAtLeastOne: 'Choose at least one option',
    otherTextRequired: 'Type your answer to use “Other”',
    duplicateOther: 'Your “Other” answer matches an option you already chose',
};
/**
 * Non-actionable fallback when an ask_user payload fails the strict
 * form parser — never silently drop questions, never render a broken
 * form. The operator channel (console/admin side) can still answer.
 */
export const ASK_USER_FALLBACK_TITLE = 'This request can’t be answered here';
export const ASK_USER_FALLBACK_BODY = 'Someone on your team can respond to it from your internal tools.';
//# sourceMappingURL=copy.js.map